import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { TrendingUp, TrendingDown, Wallet, Sparkles, ArrowRight } from 'lucide-react';
import api from '../utils/api';

const AVXMiniWidget = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    load();
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
  }, []);

  const load = async () => {
    try {
      const r = await api.get('/avx/wallet/me');
      setData(r.data);
      setError(null);
    } catch (e) {
      setError('Could not load AVX data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) return null;
  if (error) return null;

  const w = data?.wallet || {};
  const portfolioValue = data?.portfolio_value_at || 0;
  const totalInvested = (data?.holdings || []).reduce((s, h) => s + (h.total_invested || 0), 0);
  const pnl = portfolioValue - totalInvested;
  const pnlPct = totalInvested > 0 ? (pnl / totalInvested) * 100 : 0;
  const totalNetWorth = (w.at_balance || 0) + portfolioValue + (w.commission_balance || 0);

  return (
    <div
      className="relative overflow-hidden rounded-2xl border-2 border-yellow-500/40 bg-slate-900 p-4 sm:p-5 mb-6"
      data-testid="avx-mini-widget"
    >
      {/* FANTASY badge */}
      <div className="absolute top-3 right-3 flex flex-col items-end gap-1">
        <span className="text-[9px] font-bold bg-yellow-500 text-black px-2 py-0.5 rounded-full">FANTASY</span>
        <span className="text-[9px] font-bold bg-emerald-500/30 text-emerald-300 px-2 py-0.5 rounded-full flex items-center gap-1">
          <span className="w-1 h-1 rounded-full bg-emerald-400 animate-pulse"></span>
          LIVE
        </span>
      </div>

      <div className="flex items-center gap-2 mb-3">
        <Sparkles className="w-5 h-5 text-yellow-400" />
        <h3 className="font-bold text-base sm:text-lg bg-gradient-to-r from-yellow-400 to-orange-400 bg-clip-text text-transparent">
          AVX — Your Trading Snapshot
        </h3>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
        <Stat
          label="Net Worth"
          value={`${totalNetWorth.toLocaleString(undefined, { maximumFractionDigits: 2 })} AT`}
          color="text-yellow-300"
          testid="widget-networth"
        />
        <Stat
          label="AT Balance"
          value={`${(w.at_balance || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`}
          color="text-white"
          testid="widget-at"
        />
        <Stat
          label="Portfolio"
          value={`${portfolioValue.toLocaleString(undefined, { maximumFractionDigits: 2 })} AT`}
          color="text-blue-300"
          testid="widget-portfolio"
        />
        <Stat
          label="P/L"
          value={
            <span className={pnl >= 0 ? 'text-emerald-400' : 'text-red-400'}>
              {pnl >= 0 ? '+' : ''}{pnl.toFixed(2)} ({pnlPct.toFixed(1)}%)
            </span>
          }
          icon={pnl >= 0 ? <TrendingUp className="w-3.5 h-3.5" /> : <TrendingDown className="w-3.5 h-3.5" />}
          color={pnl >= 0 ? 'text-emerald-400' : 'text-red-400'}
          testid="widget-pnl"
        />
      </div>

      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="text-xs text-slate-400">
          Holdings: <strong className="text-white">{(data?.holdings || []).length}</strong> assets
          {(w.commission_balance || 0) > 0 && (
            <span className="ml-2">• Commission earned: <strong className="text-purple-300">{(w.commission_balance || 0).toFixed(2)} AT</strong></span>
          )}
        </div>
        <Link
          to="/avx"
          className="inline-flex items-center gap-1 px-3 py-1.5 bg-gradient-to-r from-yellow-500 to-orange-500 text-black font-semibold rounded-lg text-sm hover:scale-105 transition"
          data-testid="widget-open-avx"
        >
          Open AVX
          <ArrowRight className="w-3.5 h-3.5" />
        </Link>
      </div>
    </div>
  );
};

const Stat = ({ label, value, color, icon, testid }) => (
  <div className="bg-slate-900/40 rounded-lg p-2 border border-slate-700/50" data-testid={testid}>
    <div className="text-[10px] uppercase tracking-wide text-slate-400 flex items-center gap-1">
      {icon}{label}
    </div>
    <div className={`text-sm sm:text-base font-bold mt-0.5 ${color}`}>{value}</div>
  </div>
);

export default AVXMiniWidget;
